/**
 * Convert a user-entered wall-clock instant in a chosen IANA timezone to a
 * UTC `Date`. Used by PRD-001's date-filter subject-selection mode to build
 * the `createdStart` parameter for the patient-registry subject-search call.
 *
 * Algorithm (trinity BLOCKER + morpheus IMPORTANT from the impl panel):
 *   1. Treat the user's wall-clock as if it were UTC. Call this `guess`.
 *   2. Format `guess` (as a real UTC instant) into the target TZ. The
 *      formatted wall-clock will differ from the user's input by the TZ
 *      offset; that difference is the offset itself.
 *   3. Subtract the offset from `guess` to get the real UTC instant.
 *   4. Re-verify by formatting the real UTC instant in the target TZ: the
 *      formatted wall-clock should match the user's input. (For DST
 *      transitions this resolves correctly because the second iteration
 *      computes the offset at the actual UTC instant, not at `guess`.)
 *
 * DST edge cases:
 *   - **Ambiguous fall-back** (e.g., 1:30 AM on a fall-back day occurs
 *     twice in the local timezone): we return the first occurrence (the
 *     pre-transition offset). Matches ECMA-262 convention for
 *     `Date.UTC(...)` ambiguity resolution.
 *   - **Nonexistent spring-forward** (e.g., 2:30 AM on a spring-forward
 *     day doesn't exist locally): we return the instant that the formatter
 *     emits when asked, which is the post-jump equivalent.
 *
 * Intl fallback: if `timeZone` is invalid or the runtime doesn't recognize
 * it, the formatter throws RangeError. Caller is responsible for catching
 * (or providing a known-good zone — `Intl.supportedValuesOf("timeZone")`).
 */
export function localInstantToUtc(input: {
    year: number;
    month: number; // 1-12
    day: number;
    hour: number;
    minute: number;
    timeZone: string;
}): Date {
    const { year, month, day, hour, minute, timeZone } = input;
    // The user's local wall-clock, expressed as if it were UTC. This is NOT
    // the answer — it's the seed for offset computation.
    const guessMs = Date.UTC(year, month - 1, day, hour, minute, 0);
    const offsetMs = tzOffsetMs(guessMs, timeZone);
    // First-pass UTC: subtract the offset computed at `guess`. For non-DST
    // transitions this is exact.
    const firstPassUtcMs = guessMs - offsetMs;
    // Second pass converges across DST transitions: recompute the offset at
    // the candidate UTC instant. If the offset hasn't changed, this equals
    // the first-pass result.
    const secondPassOffsetMs = tzOffsetMs(firstPassUtcMs, timeZone);
    return new Date(guessMs - secondPassOffsetMs);
}

/**
 * Offset (in milliseconds) of `timeZone` from UTC at the given UTC instant.
 * Positive when ahead of UTC (Asia/Tokyo => +9h => +32_400_000).
 *
 * Computed by formatting the UTC instant in the target TZ and back into a
 * synthetic `Date.UTC(...)` value; the difference is the offset.
 */
function tzOffsetMs(utcMs: number, timeZone: string): number {
    const dtf = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
    const parts = dtf.formatToParts(new Date(utcMs));
    const get = (type: string): number => {
        const part = parts.find((p) => p.type === type);
        if (!part) throw new Error(`localInstantToUtc: missing ${type} from Intl formatter`);
        return Number(part.value);
    };
    const localUtcLike = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
    return localUtcLike - utcMs;
}

/**
 * Detect the browser's IANA timezone with a UTC fallback. Some locked-down
 * browsers (kiosk modes, certain enterprise policies) report an empty
 * string or `undefined`; ship a deterministic default rather than throwing.
 */
export function detectBrowserTimeZone(): string {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return tz && tz.length > 0 ? tz : "UTC";
    } catch {
        return "UTC";
    }
}
